import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import Typography from '../../../../components/Typography';
import TextInput from '../../../../components/TextInput';
import Button from '../../../../components/CustomButton';
import Segmented from '../../../../components/segmented';
import ButtonGroup from '../../../../components/ButtonGroup';
import AttachMaterials from '../../../../components/AttachMaterials';
import MuiSegmented from '../../../../components/MUISegmented';

// Styles
import styles from './style.module.scss';

// Assets
import BrainRotPng from '../../../../components/assets/generate/video_style/brainrot.png';
import ExplainerPng from '../../../../components/assets/generate/video_style/explainer.png';
import CartoonPng from '../../../../components/assets/generate/video_style/cartoon.png';
import PublicTalkPng from '../../../../components/assets/generate/video_style/public_talk.png';


// Types
import { VideoStyle } from '../../../types';
import ProcessingComponent from '../../../../components/ProcessingComponent';
import Player from './Player';
import { Link, Note, UploadFile } from '../../../../types';

// Utils
import useCsrfToken from '../../../../utils/hooks/csfrToken';
import * as urls from '../../../../utils/urls';
import actionCableConsumer from '../../../../utils/actionCable';

const ButtonGroupOption = ({ label, value }: { label: string, value: any }) => {
  // Hooks
  const { t } = useTranslation();

  // States
  let icon = null;
  let description = '--';
  switch (value) {
    case 'brain_rot':
      icon = <img src={BrainRotPng} alt='Brain Rot' className={`${styles.style_option_icon} ${styles.brain_rot}`} />;
      description = t('generate.video.style.brain_rot.description');
      break;
    case 'explainer':
      icon = <img src={ExplainerPng} alt='Explainer' className={`${styles.style_option_icon} ${styles.explainer}`} />;
      description = t('generate.video.style.explainer.description');
      break;
    case 'cartoon':
      icon = <img src={CartoonPng} alt='Cartoon' className={`${styles.style_option_icon} ${styles.cartoon}`} />;
      description = t('generate.video.style.cartoon.description');
      break;
    case 'public_talk':
      icon = <img src={PublicTalkPng} alt='Public Talk' className={`${styles.style_option_icon} ${styles.public_talk}`} />;
      description = t('generate.video.style.public_talk.description');
      break;
    default:
      icon = null;
      description = '--';
      break;
  }

  return (
    <div className={styles.style_option}>
      {icon}
      <div className={styles.style_option_content}>
        <Typography variant='p' className={styles.style_option_title}>
          {label}
        </Typography>

        <Typography variant='p' className={styles.style_option_description}>
          {description}
        </Typography>
      </div>
    </div>
  )
}

const VideoView: React.FC = () => {
  // Hooks
  const { t } = useTranslation();
  const csrfToken = useCsrfToken();

  // States
  const [view, setView] = useState<'form' | 'player'>('form');
  const [processing, setProcessing] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
  const [processingError, setProcessingError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [voiceType, setVoiceType] = useState<'male' | 'female'>('male');
  const [length, setLength] = useState<number>(15);
  const [style, setStyle] = useState<VideoStyle>('brain_rot');
  const [notes, setNotes] = useState<Note[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [links, setLinks] = useState<Link[]>([]);

  const [error, setError] = useState<{ title: boolean }>({ title: false });

  const [video, setVideo] = useState<UploadFile | null>(null);

  // Ref for ActionCable subscription
  const subscription = useRef<ActionCable.Subscription | null>(null);

  // Memoized Values
  const voiceTypeOptions = useMemo(() => [
    { label: t('generate.video.voice_type.male'), value: 'male' },
    { label: t('generate.video.voice_type.female'), value: 'female' },
  ], [t]);

  const lengthOptions = useMemo(() => [
    { label: t('generate.video.length.15'), value: 15 },
    { label: t('generate.video.length.30'), value: 30 },
    { label: t('generate.video.length.1min'), value: 60 },
    { label: t('generate.video.length.3min'), value: 180 },
  ], [t]);

  const styleOptions = useMemo(() => [
    { label: t('generate.video.style.brain_rot.title'), value: 'brain_rot' },
    { label: t('generate.video.style.explainer.title'), value: 'explainer' },
    { label: t('generate.video.style.cartoon.title'), value: 'cartoon' },
    { label: t('generate.video.style.public_talk.title'), value: 'public_talk' },
  ], [t]);

  // ActionCable message handler
  const videoGenerationReceived = useCallback((data: any) => {
    // Only handle messages for the current job
    if (data.job_id !== currentJobId) return;

    switch (data.type) {
      case 'video_generation_progress':
        // Keep processing state true while generating
        // Reset any previous errors
        setProcessingError(null);
        break;

      case 'video_generation_complete':
        // Video generation completed successfully
        // Set video data and view first, then stop processing
        setVideo(data.video_data);
        setView('player');
        setProcessing(false);
        setProcessingStartTime(null);
        setProcessingError(null);
        break;

      case 'video_generation_error':
        // Video generation failed
        setProcessing(false);
        setProcessingStartTime(null);
        setProcessingError(data.error || 'Video generation failed');
        console.error('Video generation failed:', data.error);
        break;

      default:
    }
  }, [currentJobId]);

  // Handlers
  const handleChangeTitle = (value: string) => {
    setTitle(value);
    setError(prev => ({ ...prev, title: false }));
  }
  const handleChangeVoiceType = (value: any) => setVoiceType(value as 'male' | 'female');
  const handleChangeLength = (value: any) => setLength(value as number);
  const handleChangeStyle = (value: any) => setStyle(value as VideoStyle);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (error.title) return;

    setProcessing(true);
    setProcessingStartTime(Date.now());
    setProcessingError(null);

    try {
      // Get fresh CSRF token from meta tag
      const metaToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;

      const formData = new FormData();
      formData.append('video[title]', title);
      formData.append('video[voice_type]', voiceType);
      formData.append('video[length]', length.toString());
      formData.append('video[style]', style);

      if (notes.length > 0) notes.forEach(note => formData.append('video[notes][]', note.id.toString()));
      if (uploadedFiles.length > 0) uploadedFiles.forEach(file => formData.append('video[uploaded_files][]', file));
      if (links.length > 0) links.forEach(link => formData.append('video[links][]', link.url));

      const url = urls.videosUrl();
      const options = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': metaToken || csrfToken, // Use fresh token or fallback
          // Note: Don't set Content-Type for FormData - let browser set it automatically
        },
        body: formData,
      }

      const response = await fetch(url, options);

      if (response.ok) {
        const data = await response.json();
        // Store the job ID to track this specific generation
        setCurrentJobId(data.id);
        // Processing state is maintained until we receive completion message
      } else {
        const errorText = await response.text();
        console.error('Video generation failed:', response.status, errorText);
        setProcessing(false);
        setProcessingStartTime(null);
        setProcessingError(`Request failed: ${response.status} ${errorText}`);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
    } catch (error) {
      console.error('Video generation request failed:', error);
      setProcessing(false);
      setProcessingStartTime(null);
      setProcessingError(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  }, [title, csrfToken, voiceType, length, style, notes, uploadedFiles, links]);

  const handleChangeNotes = useCallback((notes: Note[]) => {
    setNotes(notes);
  }, []);

  const handleChangeUploadedFiles = useCallback((files: string[]) => {
    setUploadedFiles(files);
  }, []);

  const handleChangeLinks = useCallback((links: Link[]) => {
    setLinks(links);
  }, []);

  // Timeout handler for stuck video generation
  useEffect(() => {
    if (!processing || !processingStartTime) return;

    // Set timeout for 8 minutes (longer than the job's 5-minute timeout + retries)
    const timeoutId = setTimeout(() => {
      if (processing) {
        console.warn('Video generation timed out after 8 minutes');
        setProcessing(false);
        setProcessingStartTime(null);
        setProcessingError('Video generation timed out. This might be due to high server load or network issues. Please try again.');
      }
    }, 8 * 60 * 1000); // 8 minutes

    return () => clearTimeout(timeoutId);
  }, [processing, processingStartTime]);

  // Set up ActionCable subscription for video generation updates
  useEffect(() => {
    // Get current user ID from a data attribute or global variable
    const userId = document.querySelector('meta[name="current-user-id"]')?.getAttribute('content');

    if (!userId) {
      console.warn('No user ID found for video generation subscription');
      return;
    }

    subscription.current = actionCableConsumer.subscriptions.create(
      { channel: 'VideoGenerationChannel', user_id: userId },
      {
        received: videoGenerationReceived,
        connected: () => {
        },
        disconnected: () => {
          // If we're processing and lose connection, show a warning
          if (processing) {
            console.warn('WebSocket connection lost during video generation');
          }
        }
      }
    );

    return () => {
      subscription.current?.unsubscribe();
    };
  }, [videoGenerationReceived, processing]);

  // Recovery handler for stuck/failed generation
  const handleRetry = useCallback(() => {
    setProcessingError(null);
    setCurrentJobId(null);
    // Don't automatically restart - let user click generate again
  }, []);

  const handleCancel = useCallback(() => {
    setProcessing(false);
    setProcessingStartTime(null);
    setProcessingError(null);
    setCurrentJobId(null);
  }, []);

  // Show error state if there's an error
  if (processingError && !processing) {
    return (
      <div className={styles.root}>
        <div className={styles.error_container}>
          <Typography variant='h3' className={styles.error_title}>
            {t('generate.video.error_title')}
          </Typography>

          <Typography variant='p' className={styles.error_message}>
            {processingError}
          </Typography>

          <div className={styles.error_actions}>
            <Button
              variant='secondary'
              onClick={handleRetry}
              classes={{ root: styles.error_button }}
            >
              {t('generate.video.try_again')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Always show processing component while processing, regardless of other states
  if (processing) {
    const elapsedTime = processingStartTime ? Math.floor((Date.now() - processingStartTime) / 1000) : 0;
    const minutes = Math.floor(elapsedTime / 60);
    const seconds = elapsedTime % 60;
    const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    return (
      <div className={styles.root}>
        <div className={styles.processing_container}>
          <ProcessingComponent message={t('generate.video.processing_message')} />

          <div className={styles.processing_info}>
            <Typography variant='p' className={styles.processing_time}>
              {t('generate.video.processing_time', { time: timeDisplay })}
            </Typography>

            {elapsedTime > 120 && ( // Show after 2 minutes
              <Typography variant='p' className={styles.processing_note}>
                {t('generate.video.processing_note')}
              </Typography>
            )}

            <Button
              variant='secondary'
              onClick={handleCancel}
              classes={{ root: styles.cancel_button }}
            >
              {t('generate.video.cancel')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Only show player when not processing and video is ready
  if (view === 'player' && video && !processing) {
    return (
      <div className={styles.root}>
        <Player
          video={video}
          onBack={() => {
            setView('form');
            setVideo(null);
            setCurrentJobId(null);
            setProcessingError(null);
            setProcessingStartTime(null);
          }}
        />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.form_container}>
          <div className={styles.form_inputs}>
            <TextInput
              label={t('generate.video.title_label')}
              value={title}
              onChange={handleChangeTitle}
              error={error.title ? t('generate.video.title_error') : ''}
              required
            />

            <div className={styles.form_item}>
              <Typography variant='p'>{t('generate.video.voice_label')}</Typography>

              <MuiSegmented
                options={voiceTypeOptions}
                value={voiceType}
                onChange={handleChangeVoiceType}
              />
            </div>

            <div className={styles.form_item}>
              <Typography variant='p'>{t('generate.video.style_label')}</Typography>

              <ButtonGroup
                options={styleOptions}
                value={style}
                onChange={handleChangeStyle}
                onRenderOptionContent={(option) => <ButtonGroupOption label={option.label} value={option.value} />}
              />
            </div>

            {/* <div className={styles.form_item}>
              <Typography variant='p'>{t('generate.video.length_label')}</Typography>

              <MuiSegmented
                options={lengthOptions}
                value={length}
                onChange={handleChangeLength}
              />
            </div> */}
          </div>

          <AttachMaterials
            onChangeNotes={handleChangeNotes}
            onChangeUploadedFiles={handleChangeUploadedFiles}
            onChangeLinks={handleChangeLinks}
          />
        </div>

        <div className={styles.actions}>
          <Button type='submit' variant='primary' classes={{ root: styles.actions_button }}>
            {t('generate.start_generation')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default VideoView;
